// Pay-period deduction batch — the ONLY automation in the module.
// Drafts one row per active, mapped account at its current rate.
// Nothing is written until the admin reviews and confirms; the
// server's UNIQUE(account_id, batch_id) index makes re-runs safe.
import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { api } from '../api';
import { formatMoney, formatMoneySigned } from '../utils/money';

const TODAY = () => new Date().toISOString().slice(0, 10);

export default function DamageFundDeductionBatchPage() {
  const navigate = useNavigate();
  const [date, setDate] = useState(TODAY());
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState('');
  const [saving, setSaving] = useState(false);
  const [result, setResult] = useState(null);

  const load = useCallback(async (d) => {
    setLoading(true); setErr(''); setResult(null);
    try {
      const { rows } = await api.getDamageFundDeductionDraft(d);
      // seed editable state: amount = rate, skip = recommend_skip || already_run
      setRows(rows.map(r => ({
        ...r,
        amount: r.rate,
        skip: r.recommend_skip || r.already_run,
      })));
    } catch (e) { setErr(e.message); }
    setLoading(false);
  }, []);
  useEffect(() => { load(date); }, [load, date]);

  function setRow(id, patch) {
    setRows(rs => rs.map(r => r.account_id === id ? { ...r, ...patch } : r));
  }

  const willPost = rows.filter(r => !r.skip && !r.already_run && Number(r.amount) > 0);
  const totalToPost = willPost.reduce((s, r) => s + Math.abs(Number(r.amount) || 0), 0);

  async function confirm() {
    if (willPost.length === 0) { setErr('Nothing selected to post.'); return; }
    if (!window.confirm(`Post ${willPost.length} deduction(s) totaling ${formatMoney(totalToPost)} for pay period ${date}? This appends entries to each driver's ledger.`)) return;
    setSaving(true); setErr('');
    try {
      const res = await api.confirmDamageFundDeductionBatch({
        date,
        rows: rows.map(r => ({ account_id: r.account_id, amount: Number(r.amount), skip: r.skip || r.already_run })),
      });
      setResult(res);
      await load(date); // refresh so posted rows show "already run"
      setResult(res);   // load() clears result, re-set it after
    } catch (e) { setErr(e.message); }
    setSaving(false);
  }

  return (
    <div className="df-page">
      <button type="button" className="btn btn-muted btn-sm" onClick={() => navigate('/damage-fund')} style={{ marginBottom: 16 }}>← Damage Fund</button>

      <h1 style={{ marginTop: 0 }}>Run Pay-Period Deduction</h1>
      <p style={{ color: '#64748b', marginTop: -4, maxWidth: 680 }}>
        Drafts a deduction for each active, mapped driver at their current biweekly rate.
        Review every line, adjust amounts, and skip anyone at cap. Nothing is written until you confirm.
      </p>

      <div style={{ display: 'flex', alignItems: 'flex-end', gap: 12, margin: '16px 0' }}>
        <div>
          <label className="form-label" style={{ display: 'block', fontSize: 13, fontWeight: 600, marginBottom: 4 }}>Pay-Period Date</label>
          <input className="form-input" type="date" value={date} onChange={e => setDate(e.target.value)}
            style={{ padding: '10px 14px', border: '1.5px solid #e2e8f0', borderRadius: 10, fontSize: 14 }} />
        </div>
        <div style={{ marginLeft: 'auto', textAlign: 'right' }}>
          <div style={{ fontSize: 12, color: '#64748b', textTransform: 'uppercase', letterSpacing: 0.5, fontWeight: 600 }}>Will Post</div>
          <div style={{ fontSize: 22, fontWeight: 800, color: '#0f172a' }}>{willPost.length} · {formatMoney(totalToPost)}</div>
        </div>
      </div>

      {result && (
        <div style={{ background: '#ecfdf5', border: '1px solid #a7f3d0', borderRadius: 10, padding: '12px 16px', marginBottom: 16, color: '#065f46', fontSize: 14 }}>
          Posted <strong>{result.posted}</strong>, skipped <strong>{result.skipped}</strong>
          {result.errors?.length ? <span style={{ color: '#b91c1c' }}> · {result.errors.length} error(s): {result.errors.map(e => `#${e.account_id} ${e.error}`).join('; ')}</span> : null}
        </div>
      )}
      {err && <div style={{ color: '#ef4444', marginBottom: 16, fontSize: 14 }}>{err}</div>}

      {loading ? <div>Loading…</div> : rows.length === 0 ? (
        <div style={{ color: '#64748b' }}>No active, mapped accounts to deduct. Map drivers in Account Settings first.</div>
      ) : (
        <div className="table-wrapper">
          <table className="data-table">
            <thead>
              <tr>
                <th style={{ width: 36, textAlign: 'center' }}>Post</th>
                <th>Driver</th>
                <th>Group</th>
                <th style={{ textAlign: 'right' }}>Balance</th>
                <th style={{ textAlign: 'right' }}>Deduct ($)</th>
                <th>Note</th>
              </tr>
            </thead>
            <tbody>
              {rows.map(r => {
                const disabled = r.already_run;
                const willThis = !r.skip && !disabled && Number(r.amount) > 0;
                return (
                  <tr key={r.account_id} style={{ opacity: disabled ? 0.55 : 1, background: willThis ? 'transparent' : '#f8fafc' }}>
                    <td style={{ textAlign: 'center' }}>
                      <input type="checkbox" checked={willThis} disabled={disabled}
                        onChange={e => setRow(r.account_id, { skip: !e.target.checked })}
                        style={{ width: 18, height: 18, cursor: disabled ? 'not-allowed' : 'pointer' }} />
                    </td>
                    <td style={{ fontWeight: 600 }}>{r.display_name}<div style={{ fontSize: 11, color: '#94a3b8', fontWeight: 400 }}>{r.user_full_name}</div></td>
                    <td style={{ fontSize: 12, color: '#64748b' }}>{r.region_group || '—'}</td>
                    <td style={{ textAlign: 'right', fontWeight: 700, color: r.balance < 0 ? '#ef4444' : '#10b981' }}>{formatMoneySigned(r.balance)}</td>
                    <td style={{ textAlign: 'right' }}>
                      <input type="number" min="0" step="0.01" value={r.amount} disabled={disabled || r.skip}
                        onChange={e => setRow(r.account_id, { amount: e.target.value })}
                        style={{ width: 90, padding: '6px 8px', textAlign: 'right', border: '1.5px solid #e2e8f0', borderRadius: 8, fontSize: 13 }} />
                    </td>
                    <td style={{ fontSize: 12 }}>
                      {r.already_run
                        ? <span style={{ color: '#64748b', fontWeight: 600 }}>Already run for this period</span>
                        : r.at_cap
                          ? <span style={{ color: '#f59e0b', fontWeight: 600 }}>{r.note}</span>
                          : r.underwater
                            ? <span style={{ color: '#ef4444', fontWeight: 600 }}>{r.note}</span>
                            : <span style={{ color: '#94a3b8' }}>Building</span>}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 20 }}>
        <button type="button" className="btn btn-muted" onClick={() => navigate('/damage-fund')} disabled={saving}>Cancel</button>
        <button type="button" className="btn btn-primary" onClick={confirm} disabled={saving || willPost.length === 0}>
          {saving ? 'Posting…' : `Post ${willPost.length} Deduction${willPost.length === 1 ? '' : 's'}`}
        </button>
      </div>
    </div>
  );
}
